/**
 * DB 없이 동작할 때 쓰는 초기 mock 스냅샷. 실제 DB 스냅샷과 동일한 노드·엣지 집합(lib/pipeline-graph)을 쓴다.
 * 클라이언트(시뮬레이터 초기값)·서버(DATA_MODE=mock) 양쪽에서 import 한다 — DB 코드 import 금지.
 */
import { FACTORIES, MACHINES } from "./machines";
import { nodeId, OUTPUT_DASHBOARD, OUTPUT_MONITOR, TABLE_NAMES } from "../lib/node-ids";
import { computeSummary, createBaseNodes, createEdges, deriveEdges, round1, } from "../lib/pipeline-graph";
/** 결정적 의사난수 (SSR/CSR 동일 결과) */
function mulberry32(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
export function createMockSnapshot(opts = {}) {
    const nowIso = opts.now ?? new Date().toISOString();
    const nowMs = Date.parse(nowIso);
    const rnd = mulberry32(opts.seed ?? 20260829);
    const nodes = createBaseNodes(nowIso);
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const events = [];
    const iso = (secAgo) => new Date(nowMs - secAgo * 1000).toISOString();
    // 설비: 대부분 running, 일부 healthy/offline/warning/error 섞기
    const factoryThroughput = new Map();
    MACHINES.forEach((m, i) => {
        const n = byId.get(nodeId.machine(m.factoryId, m.machineId));
        if (!n)
            return;
        const r = rnd();
        let status = "running";
        let description = "샷 수집 중 (최근 3분 내 샷)";
        let sinceLast = 5 + Math.floor(rnd() * 120);
        let shotsPerMin = round1(1 + rnd() * 2.5);
        if (r > 0.9) {
            status = "offline";
            description = "설비 정지 (OPC UA 연결 정상, online_state=0)";
            sinceLast = 3600 + Math.floor(rnd() * 20000);
            shotsPerMin = 0;
        }
        else if (r > 0.86) {
            status = "healthy";
            description = "최근 10분 내 샷 수집";
            sinceLast = 200 + Math.floor(rnd() * 380);
            shotsPerMin = round1(0.3 + rnd() * 0.7);
        }
        else if (r > 0.83) {
            status = "warning";
            description = "결번 진행 중 (연결 끊김)";
            shotsPerMin = round1(rnd() * 0.5);
            sinceLast = 600 + Math.floor(rnd() * 600);
            events.push({
                id: `mock:gap-open:${n.id}`,
                timestamp: iso(sinceLast),
                level: "warning",
                nodeId: n.id,
                message: `${m.label} 결번 진행 중 — 연결 끊김`,
            });
        }
        else if (r > 0.81 || i === 6) {
            status = "error";
            description = "연결 끊김 (conn_state=RECONNECTING)";
            shotsPerMin = 0;
            sinceLast = 900 + Math.floor(rnd() * 900);
            events.push({
                id: `mock:disconnected:${n.id}`,
                timestamp: iso(sinceLast),
                level: "error",
                nodeId: n.id,
                message: `${m.label} 연결 끊김 (RECONNECTING)`,
            });
        }
        factoryThroughput.set(m.factoryId, (factoryThroughput.get(m.factoryId) ?? 0) + shotsPerMin);
        n.data = {
            ...n.data,
            status,
            description,
            throughput: shotsPerMin,
            latency: sinceLast,
            successRate: status === "warning" ? 92.5 : 100,
            lastUpdated: iso(sinceLast),
            retryCount: status === "warning" ? 1 : 0,
            extra: {
                "마지막 샷(source_ts)": iso(sinceLast),
                "최근 10분 샷": Math.round(shotsPerMin * 10),
                "마지막 shot_count": 100000 + i * 137 + Math.floor(rnd() * 500),
                "마지막 quality": "good",
                "conn_state": status === "error" ? "RECONNECTING" : "CONNECTED",
                "online_state": status === "offline" ? 0 : 1,
                "alarm_state": 0,
                "cycle_time": round1(18 + rnd() * 30),
            },
        };
    });
    let totalPerMin = 0;
    for (const f of FACTORIES) {
        const tp = round1(factoryThroughput.get(f.id) ?? 0);
        totalPerMin += tp;
        const hbAge = Math.floor(rnd() * 20);
        const collector = byId.get(nodeId.collector(f.id));
        const outbox = byId.get(nodeId.outbox(f.id));
        const uploader = byId.get(nodeId.uploader(f.id));
        const legacy = byId.get(nodeId.legacy(f.id));
        if (!collector || !outbox || !uploader || !legacy)
            continue;
        const connected = MACHINES.filter((m) => m.factoryId === f.id && byId.get(nodeId.machine(m.factoryId, m.machineId))?.data.status !== "error").length;
        collector.data = {
            ...collector.data,
            status: "healthy",
            description: `정상 (heartbeat ${hbAge}s 전, 연결 설비 ${connected}/${f.machineCount})`,
            throughput: tp,
            latency: hbAge,
            successRate: round1((connected / f.machineCount) * 100),
            lastUpdated: iso(hbAge),
            extra: {
                "instance_id": `${f.id}-collector-01`,
                "app_version": "2.1.0-mock",
                "heartbeat ts": iso(hbAge),
                "process_start_ts": iso(86400 * 3),
                "connected_machines": connected,
                "mem_queue_depth": Math.floor(rnd() * 20),
                "dropped_total": 0,
            },
        };
        const pending = Math.floor(rnd() * 40);
        outbox.data = {
            ...outbox.data,
            status: pending > 0 ? "running" : "healthy",
            description: pending > 0 ? `전송 대기 ${pending}건` : "큐 비어 있음",
            throughput: tp,
            latency: hbAge,
            lastUpdated: iso(hbAge),
            extra: { "outbox_pending": pending, "outbox_disk_mb": 12, "mem_queue_depth": 3 },
        };
        const uploaded = Math.round(tp);
        uploader.data = {
            ...uploader.data,
            status: uploaded > 0 ? "running" : "healthy",
            description: `최근 1분 ${uploaded}행 적재`,
            throughput: uploaded,
            latency: 0,
            lastUpdated: iso(hbAge),
            extra: { "uploaded_rows_1m": uploaded, "upload_fail_1m": 0, "poison_total": 0, "dropped_total": 0 },
        };
        legacy.data = {
            ...legacy.data,
            status: tp > 0 ? "running" : "healthy",
            description: `${legacy.data.description} · 처리량은 shot_records 와 동일 가정`,
            throughput: tp,
            lastUpdated: iso(hbAge),
        };
    }
    const storageStatus = (tp) => (tp > 0 ? "running" : "healthy");
    const storageThroughput = {
        shot_records: round1(totalPerMin),
        machine_status: round1(MACHINES.length / 1),
        machine_events: round1(rnd() * 2),
        collection_gaps: 0,
        collector_heartbeat: FACTORIES.length,
    };
    for (const t of TABLE_NAMES) {
        const n = byId.get(nodeId.table(t));
        if (!n)
            continue;
        const tp = storageThroughput[t];
        n.data = { ...n.data, status: t === "collection_gaps" ? "healthy" : storageStatus(tp), throughput: tp, lastUpdated: nowIso };
    }
    const dash = byId.get(OUTPUT_DASHBOARD);
    if (dash)
        dash.data = { ...dash.data, status: "running", throughput: 30, lastUpdated: nowIso };
    const mon = byId.get(OUTPUT_MONITOR);
    if (mon)
        mon.data = { ...mon.data, status: "running", throughput: FACTORIES.length, lastUpdated: nowIso };
    events.push({
        id: "mock:boot",
        timestamp: nowIso,
        level: "info",
        message: "데모 모드 — 실제 수집 데이터가 아닌 예시 데이터로 동작 중",
    });
    events.sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
    const edges = deriveEdges(nodes, createEdges());
    return {
        mode: opts.mode ?? "mock",
        generatedAt: nowIso,
        nodes,
        edges,
        events,
        summary: computeSummary(nodes),
    };
}
/** 모듈 로드 시점의 초기 스냅샷 (테스트·스토리 용). 실제 앱은 createMockSnapshot() 을 호출한다. */
export const MOCK_PIPELINE = createMockSnapshot({ now: "2026-08-29T08:00:00.000Z" });
